/**
 * PixelFullscreen — full-viewport presentation mode for the pixel-world canvas.
 *
 * Hosts the canvas edge-to-edge with a readability scrim and a layer of toggleable,
 * map-style overlays (runs, stats, activity) floating above it. Rendered with
 * position:fixed so it escapes the resizable-panel layout and covers the viewport.
 */

import { useEffect, useState } from "react";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { Minimize2 } from "lucide-react";
import { PixelWorldCanvas } from "../PixelWorldCanvas.js";
import type { TopologyInfo } from "../../scenes/SceneLayout.js";
import { useWorldStore } from "../../../stores/world.js";
import { useTopology } from "../../../api/hooks.js";
import { StatsHud } from "./StatsHud.js";
import { RunsOverlay } from "./RunsOverlay.js";
import { EventTicker } from "./EventTicker.js";
import { LayerToggles } from "./LayerToggles.js";
import { DEFAULT_LAYERS, type OverlayLayers } from "./layers.js";
import { DEMO_MODE } from "../../../demo/flag.js";
import { DemoTopologyDropdown } from "../../../demo/DemoTopologyDropdown.js";
import { demoTopologyMeta } from "../../../demo/topologies.js";

interface PixelFullscreenProps {
  topologyOverride?: TopologyInfo;
}

export function PixelFullscreen({ topologyOverride }: PixelFullscreenProps) {
  const setFullscreen = useWorldStore((s) => s.setFullscreen);
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const { data: topology } = useTopology(selectedTopologyId ?? "");

  // The simulated build shows everything off the bat; the live app keeps its
  // calmer defaults (activity ticker off).
  const [layers, setLayers] = useState<OverlayLayers>(
    DEMO_MODE ? { runs: true, stats: true, events: true } : DEFAULT_LAYERS,
  );
  const toggle = (key: keyof OverlayLayers) =>
    setLayers((l) => ({ ...l, [key]: !l[key] }));

  // Esc exits full-screen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setFullscreen]);

  const isLive = !DEMO_MODE && !topologyOverride && !!selectedTopologyId;
  const sceneLabel = DEMO_MODE
    ? demoTopologyMeta(selectedTopologyId ?? "").label
    : topologyOverride
      ? "Demo scene"
      : (topology?.definition?.type ?? "Topology");
  const badgeKicker = DEMO_MODE ? "Simulated preview" : isLive ? "Live" : "Preview";
  const badgeDot = DEMO_MODE
    ? "bg-brand-cyan"
    : isLive
      ? "bg-brand-green shadow-glow-green"
      : "bg-text-muted";

  // Scene identity (status dot + label + topology dropdown). Rendered stacked
  // under the brand on desktop, and moved to the top-right on phones.
  const sceneBadge = (
    <div className="overlay-panel pointer-events-auto flex items-center gap-2.5 rounded-2xl px-4 py-2.5">
      <span className={`h-2 w-2 rounded-full ${badgeDot}`} />
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wide text-text-muted">{badgeKicker}</span>
        {DEMO_MODE ? (
          <DemoTopologyDropdown />
        ) : (
          <span className="text-sm font-medium text-text-primary">{sceneLabel}</span>
        )}
      </span>
    </div>
  );

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: [0.2, 0, 0, 1] }}
        className="fixed inset-0 z-fullscreen bg-surface"
      >
        {/* Canvas */}
        <div className="absolute inset-0">
          <PixelWorldCanvas topologyOverride={topologyOverride} />
        </div>

        {/* Readability vignette */}
        <div className="overlay-scrim pointer-events-none absolute inset-0 z-overlay-scrim" />

        {/* Overlay chrome — wrapper is click-through; panels opt back in */}
        <div className="pointer-events-none absolute inset-0 z-overlay">
          {/* Top-left: brand lockup (demo) + scene identity (stacked on desktop) */}
          <div className="absolute left-4 top-4 flex flex-col gap-2">
            {DEMO_MODE && (
              <div className="overlay-panel pointer-events-auto rounded-2xl px-4 py-3">
                <div className="text-base font-semibold tracking-tight text-text-primary">
                  Agents World
                </div>
                <div className="hidden text-[11px] text-text-muted sm:block">
                  Build, configure, and run multi-agent systems
                </div>
              </div>
            )}
            <div className="hidden md:block">{sceneBadge}</div>
          </div>

          {/* Scene identity on phones — top-right, balancing the brand on the
              left now that Exit is hidden there. */}
          <div className="absolute right-4 top-4 md:hidden">{sceneBadge}</div>

          {/* Stats HUD — top-center on desktop, moved to the bottom on phones so
              it doesn't collide with the brand and exit controls. */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4">
            <AnimatePresence>{layers.stats && <StatsHud key="stats" />}</AnimatePresence>
          </div>

          {/* Top-right: exit — hidden on phones (the windowed dashboard behind it
              isn't mobile-ready; keep visitors in the full-screen view). */}
          <div className="absolute right-4 top-4 hidden md:block">
            <button
              onClick={() => setFullscreen(false)}
              className="overlay-panel pointer-events-auto flex min-h-[40px] items-center gap-2 rounded-2xl px-4 py-2 text-sm text-text-secondary transition-[color,scale] duration-200 hover:text-text-primary active:scale-[0.96]"
            >
              <Minimize2 className="h-4 w-4" />
              <span>Exit</span>
              <kbd className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
                Esc
              </kbd>
            </button>
          </div>

          {/* Right: runs dock — hidden on phones (too wide; buries the canvas). */}
          <div className="absolute bottom-20 right-4 top-24 hidden w-72 md:block">
            <AnimatePresence>{layers.runs && <RunsOverlay key="runs" />}</AnimatePresence>
          </div>

          {/* Bottom-left: activity ticker — hidden on phones. */}
          <div className="absolute bottom-4 left-4 hidden md:block">
            <AnimatePresence>{layers.events && <EventTicker key="events" />}</AnimatePresence>
          </div>

          {/* Bottom-center: layer toggles — hidden on phones (nothing to toggle). */}
          <div className="absolute bottom-4 left-1/2 hidden -translate-x-1/2 md:block">
            <LayerToggles layers={layers} onToggle={toggle} />
          </div>
        </div>
      </motion.div>
    </MotionConfig>
  );
}
